import {
  CheckOutlined,
  DownOutlined,
  SearchOutlined,
  UpOutlined,
} from "@ant-design/icons";
import Button from "@components/buttons/Button";
import Pdf from "@components/other/Pdf";
import { pdf } from "@react-pdf/renderer";
import { showTotal } from "@utils/functions";
import type { TableProps as AntTableProps, TablePaginationConfig } from "antd";
import {
  Grid as AntGrid,
  Table as AntTable,
  Dropdown,
  Flex,
  Input,
  InputRef,
  Menu,
  Space,
} from "antd";
import { SearchProps } from "antd/es/input";
import type { ColumnType } from "antd/es/table";
import { FilterDropdownProps } from "antd/es/table/interface";
import { addKeyIfNotUndefined } from "common/utils/universal/object";
import { saveAs } from "file-saver";
import { themeColors } from "index";
import { concat, equals, pick, reject, values } from "ramda";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Empty from "../Empty";
import * as styles from "./Table.css";

type RowSelectionType = "checkbox" | "radio";
const { Search } = Input;

enum FileExportFormat {
  CSV = "CSV",
  EXCEL = "Excel",
  PDF = "PDF",
}
export interface CustomColumnType<T> extends ColumnType<T> {
  enableSearch?: boolean;
  excludeFromExport?: boolean;
}
export type ScuiTableColumns<T> =
  readonly (CustomColumnType<T | null> | null)[];

export interface TableProps<T extends Record<string, any>>
  extends Omit<AntTableProps<T>, "columns" | "dataSource"> {
  allowClear?: boolean;
  columns: ScuiTableColumns<T>;
  dataSource: readonly (T | null)[];
  dataTestId?: string;
  enableShowHideColumns?: boolean;
  expandable?: AntTableProps<T>["expandable"];
  exportable?: boolean;
  exportCreator?: string;
  getExportFileName?: () => string;
  getExportFileTitle?: () => string;
  pagination?: TablePaginationConfig | false | undefined;
  paginationDisplayTotal?: boolean;
  rowKey: string | ((record: T) => string);
  rowSelection?: { type?: RowSelectionType } & AntTableProps<T>["rowSelection"];
  searchable?: boolean;
  searchPlaceholder?: string;
  showColumnFiltersOnMobile?: boolean;
  lastPageFooter?: JSX.Element | React.ReactNode;
  onSearch?: SearchProps["onSearch"];
  searchMaxLength?: SearchProps["maxLength"];
  // Note: onChange is already part of AntTableProps; we explicitly add it for clarity.
  onChange?: AntTableProps<T>["onChange"];
}

const Table = <T extends Record<string, any>>({
  allowClear,
  columns,
  dataSource,
  dataTestId,
  enableShowHideColumns,
  expandable,
  exportable,
  exportCreator,
  getExportFileName,
  getExportFileTitle,
  pagination = {},
  paginationDisplayTotal,
  rowKey,
  rowSelection,
  searchable = false,
  searchPlaceholder = "Search...",
  size = "small",
  showColumnFiltersOnMobile,
  lastPageFooter,
  onSearch,
  onChange,
  searchMaxLength,
  ...props
}: TableProps<T>) => {
  const { useBreakpoint } = AntGrid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [filteredData, setFilteredData] = useState(dataSource);
  const [exportData, setExportData] = useState(dataSource);
  const [globalSearch, setGlobalSearch] = useState<string>("");
  const [filters, setFilters] = useState({});
  const [rowCount, setRowCount] = useState(filteredData.length);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // If total is passed in with pagination props use that total, otherwise fall back to current filtered row count
  const externalPagination =
    typeof pagination !== "boolean"
      ? (pagination as TablePaginationConfig)
      : undefined;
  const externalTotal = externalPagination?.total;
  const externalPageSize = externalPagination?.pageSize;

  const isServerMode = externalTotal != null;

  // Fallback to local state when external values are not provided
  const effectiveTotal = externalTotal ?? rowCount;
  const effectivePageSize = externalPageSize ?? pageSize;

  const totalPages = useMemo(() => {
    return Math.ceil((effectiveTotal || 0) / (effectivePageSize || 10));
  }, [effectiveTotal, effectivePageSize]);

  // When server total is present (data is dynamically fetched), clamp the dataSource to the current page size to avoid Antd async mode datasource length warning.
  const pageLimitedDataSource =
    isServerMode && (filteredData as T[]).length > effectivePageSize
      ? ((filteredData as T[]).slice(0, effectivePageSize) as readonly T[])
      : (filteredData as readonly T[]);

  const shouldShowPagination = effectiveTotal > PAGINATION_THRESHOLD;

  const searchInput = useRef<InputRef>(null);

  useEffect(() => {
    setFilteredData(dataSource);
    setExportData(dataSource);
    setRowCount(dataSource.length);

    if (globalSearch) {
      handleGlobalSearch(globalSearch);
    }
  }, [dataSource]);

  const handleGlobalSearch = (value: string) => {
    setFilters({});

    const lower = value.toLowerCase();
    const filtered = dataSource.filter((item) =>
      Object.values(item ?? {}).some((val) =>
        String(val).toLowerCase().includes(lower)
      )
    );

    if (globalSearch === " ") {
      clearGlobalSearch();
    } else {
      setGlobalSearch(value);
      setFilteredData(filtered);
      setRowCount(filtered.length);
      setExportData(filtered);
    }
  };

  const clearGlobalSearch = () => {
    setGlobalSearch("");
    setFilteredData(dataSource);
    setRowCount(dataSource.length);
    setExportData(dataSource);
  };

  const handleClearAllFilters = (
    confirm: FilterDropdownProps["confirm"],
    close: () => void
  ) => {
    setFilters({});
    clearGlobalSearch();
    confirm();
    close();
  };

  const handleColumnSearch = (
    confirm: FilterDropdownProps["confirm"],
    dataIndex: string,
    filterValue: React.Key[]
  ) => {
    clearGlobalSearch();
    setFilters((prev) => ({ ...prev, [dataIndex]: filterValue }));
    confirm();
  };

  const handleColumnReset = (
    confirm: FilterDropdownProps["confirm"],
    clearFilters: () => void,
    dataIndex: string
  ) => {
    setFilters((prev) => ({
      ...prev,
      [dataIndex]: undefined,
    }));
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (
    data: CustomColumnType<T>
  ): CustomColumnType<T> => ({
    filteredValue: filters[data.dataIndex as keyof typeof filters] || null,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        className={styles.columnSearchContainer}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleColumnSearch(confirm, String(data.dataIndex), selectedKeys)
          }
          placeholder={`Search ${data.title}`}
          ref={searchInput}
          value={selectedKeys[0]}
        />
        <Space>
          <Button
            icon={<SearchOutlined />}
            onClick={() =>
              handleColumnSearch(confirm, String(data.dataIndex), selectedKeys)
            }
            size="small"
            style={{ width: 90 }}
            variant="primary"
          >
            Search
          </Button>
          <Button
            data-testid={"clear-column-search"}
            onClick={() =>
              clearFilters &&
              handleColumnReset(confirm, clearFilters, String(data.dataIndex))
            }
            size="small"
            style={{ width: 90 }}
            variant="secondary"
          >
            Clear
          </Button>
          <Button
            data-testid={"clear-all-column-search"}
            onClick={() => handleClearAllFilters(confirm, close)}
            size="small"
            style={{ width: 90 }}
            variant="secondary"
          >
            Clear All
          </Button>
          <Button
            data-testid={"close-column-search"}
            onClick={() => {
              close();
            }}
            size="small"
            variant="text"
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[data.dataIndex as keyof T]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (open) => {
      if (open && isMobile) {
        setTimeout(() => {
          const el = document.querySelector(
            ".ant-dropdown-placement-bottomLeft"
          );

          if (el) {
            const computed = window.getComputedStyle(el);
            let topValue;
            const inset = computed.getPropertyValue("inset");

            if (
              inset &&
              inset !== "auto" &&
              inset !== "initial" &&
              inset !== ""
            ) {
              topValue = inset.split(" ")[0];
            } else {
              topValue = computed.getPropertyValue("top");
            }

            if (el instanceof HTMLElement) {
              el.style.inset = `${topValue} auto auto auto`;
            }
          }
        }, 0);
      }
      setTimeout(() => searchInput.current?.select(), 100);
    },
  });

  const enhancedColumns = columns.map((col) => {
    if (!col) return null;
    if (!col.dataIndex) return col;

    const shouldShowColumnFilters =
      (showColumnFiltersOnMobile || !isMobile) && col.enableSearch;

    // Add a default sorter if not provided
    let defaultSorter;
    if (col.sorter === undefined && typeof col.dataIndex === "string") {
      defaultSorter = (a: T | null, b: T | null) => {
        const aValue = a?.[col.dataIndex as keyof T];
        const bValue = b?.[col.dataIndex as keyof T];

        // Handle undefined/null
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return -1;
        if (bValue === null) return 1;

        // String comparison (case-insensitive)
        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue, undefined, {
            sensitivity: "base",
          });
        }

        // Number comparison
        if (typeof aValue === "number" && typeof bValue === "number") {
          return aValue - bValue;
        }

        // Fallback to string comparison
        return String(aValue).localeCompare(String(bValue), undefined, {
          sensitivity: "base",
        });
      };
    }

    return {
      ...col,
      ...(typeof col.dataIndex === "string" && shouldShowColumnFilters
        ? getColumnSearchProps(col as CustomColumnType<T>)
        : {}),
      ...(defaultSorter ? { sorter: defaultSorter } : {}),
      ...(defaultSorter || col.sorter
        ? {
            showSorterTooltip: {
              color: "white",
              overlayInnerStyle: {
                color: "black",
              },
            },
          }
        : {}),
    };
  });

  const [availableColumns, setAvailableColumns] = useState<string[]>(
    enhancedColumns.map((column) => String(column?.key))
  );

  const showHideColumnOptions = useMemo(
    () =>
      enableShowHideColumns
        ? enhancedColumns.map((column) => ({
            label: String(column?.title ?? ""),
            value: String(column?.key),
          }))
        : [],
    [enableShowHideColumns, enhancedColumns]
  );

  const extractDataRows = (columns: string[]) =>
    exportData.map((row) => values(pick(columns, row))) as string[][];

  const constructWorkbook = (
    title: string,
    columns: { dataIndex?: string; title: string }[]
  ) => {
    const workbook = XLSX.utils.book_new();

    const dataColumns = columns.map((column) => column.dataIndex ?? "");
    const headerColumns = columns.map((column) => column.title ?? "");
    const rows = extractDataRows(dataColumns);

    const worksheet = XLSX.utils.aoa_to_sheet(concat([headerColumns], rows));

    workbook.Props = { Title: title };
    XLSX.utils.book_append_sheet(workbook, worksheet);
    return workbook;
  };

  const exportWorkbook = (format: FileExportFormat) => {
    const columns = enhancedColumns
      .filter(
        (column) =>
          availableColumns.includes(String(column?.key)) &&
          !column?.excludeFromExport
      )
      .map((column) => ({
        dataIndex: String(column?.dataIndex),
        title: String(column?.title),
      }));
    return XLSX.writeFile(
      constructWorkbook(
        getExportFileTitle ? getExportFileTitle() : "",
        columns
      ),
      `${getExportFileName ? getExportFileName() : ""}.${
        format === FileExportFormat.CSV ? "csv" : "xlsx"
      }`
    );
  };

  const exportOptions = [
    {
      label: FileExportFormat.CSV,
      onClick: () => exportWorkbook(FileExportFormat.CSV),
    },
    {
      label: FileExportFormat.EXCEL,
      onClick: () => exportWorkbook(FileExportFormat.EXCEL),
    },
    {
      label: FileExportFormat.PDF,
      onClick: async () => {
        const columns = enhancedColumns
          .filter(
            (column) =>
              availableColumns.includes(String(column?.key)) &&
              !column?.excludeFromExport
          )
          .map((column) => ({
            dataIndex: String(column?.dataIndex),
            title: String(column?.title),
          }));
        const totalColumns = columns.length;

        const blob = await pdf(
          <Pdf
            title={getExportFileTitle ? getExportFileTitle() : ""}
            columnTitles={columns.map((column) => column.title)}
            data={extractDataRows(columns.map((column) => column.dataIndex))}
            widthPercentages={columns.map(() => 100 / totalColumns)}
            creator={exportCreator ?? ""}
          />
        ).toBlob();

        saveAs(blob, `${getExportFileName ? getExportFileName() : ""}.pdf`);
      },
    },
  ];

  const [showHideColumnOpen, setShowHideColumnOpen] = useState(false);

  const showTableActions = exportable || enableShowHideColumns;

  return (
    <div>
      {searchable && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <Search
            value={globalSearch}
            placeholder={searchPlaceholder}
            onChange={(e) => setGlobalSearch(e.target.value)}
            onSearch={
              onSearch ? onSearch : (value) => handleGlobalSearch(value)
            }
            maxLength={searchMaxLength}
            style={{ flex: 1 }}
            allowClear
            {...addKeyIfNotUndefined("data-testid", `search-${dataTestId}`)}
            aria-label="Search table"
          />
        </div>
      )}
      {showTableActions ? (
        <Flex
          style={{ background: "white" }}
          gap={"1rem"}
          justify="flex-end"
          align="flex-end"
        >
          {exportable ? (
            <Dropdown
              menu={{
                items: exportOptions.map((option, index) => ({
                  key: index,
                  label: (
                    <span
                      style={{
                        color: "inherit",
                        fontWeight: "normal",
                      }}
                    >
                      {option.label}
                    </span>
                  ),
                  onClick: option.onClick,
                })),
              }}
              trigger={["click"]}
            >
              <Button variant="text">Export</Button>
            </Dropdown>
          ) : null}
          {enableShowHideColumns ? (
            <Dropdown
              open={showHideColumnOpen}
              dropdownRender={() => (
                <Menu
                  multiple
                  selectedKeys={availableColumns}
                  onClick={({ key }) => {
                    if (availableColumns.includes(key)) {
                      setAvailableColumns((columns) =>
                        reject(equals(key), columns)
                      );
                    } else {
                      setAvailableColumns((columns) => [...columns, key]);
                    }
                  }}
                >
                  {showHideColumnOptions.map((option) => (
                    <Menu.Item
                      style={{
                        backgroundColor: availableColumns.includes(option.value)
                          ? themeColors.default.info.light
                          : "inherit",
                      }}
                      key={option.value}
                      icon={
                        availableColumns.includes(option.value) ? (
                          <CheckOutlined />
                        ) : null
                      }
                    >
                      <span
                        style={{
                          color: "inherit",
                          fontWeight: "normal",
                        }}
                      >
                        {option.label}
                      </span>
                    </Menu.Item>
                  ))}
                </Menu>
              )}
            >
              <Button
                variant="text"
                onClick={() => {
                  setShowHideColumnOpen((open) => !open);
                }}
              >
                Show/Hide Columns
                {showHideColumnOpen ? <UpOutlined /> : <DownOutlined />}
              </Button>
            </Dropdown>
          ) : null}
        </Flex>
      ) : null}
      <AntTable
        aria-label="Data Table"
        bordered={true}
        columns={(enhancedColumns as CustomColumnType<T>[]).filter((column) =>
          availableColumns.includes(String(column?.key))
        )}
        dataSource={pageLimitedDataSource}
        {...addKeyIfNotUndefined("data-testid", dataTestId)}
        expandable={expandable}
        locale={{ emptyText: <Empty /> }}
        onChange={(antPagination, antFilters, antSorter, extra) => {
          setRowCount(extra.currentDataSource.length);
          setExportData(extra.currentDataSource);
          // Bubble up to parent so consumers can load data on page/sort/filter changes
          onChange?.(antPagination, antFilters, antSorter, extra);
        }}
        // Only show pagination when there are more than 10 rows (by total when provided)
        pagination={
          shouldShowPagination
            ? {
                ...pagination,
                current: currentPage,
                pageSize: pageSize,
                total: effectiveTotal,
                onChange: (page, nextPageSize) => {
                  setCurrentPage(page);
                  setPageSize(nextPageSize);
                },
                showSizeChanger: true,
                showTotal: paginationDisplayTotal ? showTotal : undefined,
                style: {
                  background: "white",
                  borderLeft: "1px solid #f0f0f0",
                  borderRadius: "0 0 8px 8px",
                  borderRight: "1px solid #f0f0f0",
                  margin: 0,
                  padding: "1rem",
                },
              }
            : false
        }
        rowKey={rowKey}
        rowSelection={rowSelection}
        scroll={{ x: "max-content" }}
        size={size}
        // Only show footer when there are more than 10 rows and on the last page
        {...(currentPage === totalPages &&
          effectiveTotal > PAGINATION_THRESHOLD && {
            footer: () =>
              lastPageFooter ? (
                lastPageFooter
              ) : (
                <div className={styles.footer}>End of Results</div>
              ),
          })}
        {...props}
      />
    </div>
  );
};
const PAGINATION_THRESHOLD = 10;

export default Table;
